import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Truck, Package, Send, Mail, Printer, ExternalLink, Loader2, AlertTriangle, Check, DollarSign, RotateCcw, MapPin, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const escapeHtml = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

function buildPackingSlipHtml(d) {
  const ship = d.ship_to || {};
  const addrLines = [
    ship.name,
    ship.line1,
    ship.line2,
    [ship.city, ship.state, ship.postal_code].filter(Boolean).join(", "),
    ship.country,
  ].filter(Boolean).map((l) => `<div>${escapeHtml(l)}</div>`).join("");
  const rows = (d.items || []).map((it) => {
    const variant = [it.size && `Size ${it.size}`, it.color].filter(Boolean).join(" · ");
    const line = it.unit_price != null ? `$${(it.unit_price * it.quantity).toFixed(2)}` : "";
    return `<tr>
      <td style="padding:10px 8px;border-bottom:1px solid #E5E2DC;text-align:center;font-weight:600;">${it.quantity}</td>
      <td style="padding:10px 8px;border-bottom:1px solid #E5E2DC;">
        <div style="font-weight:600;">${escapeHtml(it.name)}</div>
        ${variant ? `<div style="font-size:12px;color:#6B6E6B;margin-top:2px;">${escapeHtml(variant)}</div>` : ""}
      </td>
      <td style="padding:10px 8px;border-bottom:1px solid #E5E2DC;text-align:right;">${line}</td>
    </tr>`;
  }).join("");
  const dt = d.created_at ? new Date(d.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "";
  const tracking = d.tracking_number ? `<div style="margin-top:8px;font-size:12px;color:#6B6E6B;">Tracking: <strong>${escapeHtml(d.carrier || "")} ${escapeHtml(d.tracking_number)}</strong></div>` : "";
  return `<!doctype html><html><head><meta charset="utf-8"/><title>Packing Slip — ${escapeHtml(d.order_ref)}</title>
<style>
  @page { size: Letter; margin: 0.5in; }
  body { font-family: -apple-system, Helvetica, Arial, sans-serif; color: #1A1C1A; margin: 0; padding: 32px; }
  .brand { font-family: Georgia, serif; font-size: 28px; font-weight: 300; letter-spacing: -0.5px; }
  .tag { font-size: 10px; letter-spacing: 3px; text-transform: uppercase; color: #6B6E6B; margin-top: 4px; }
  .rule { border-top: 2px solid #1A1C1A; margin: 20px 0; }
  .grid { display: flex; justify-content: space-between; gap: 32px; }
  .col { flex: 1; }
  .lbl { font-size: 10px; letter-spacing: 2px; text-transform: uppercase; color: #6B6E6B; font-weight: 600; margin-bottom: 6px; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; }
  th { text-align: left; font-size: 10px; letter-spacing: 2px; text-transform: uppercase; color: #6B6E6B; padding: 8px; border-bottom: 2px solid #1A1C1A; }
  .totals { margin-top: 12px; margin-left: auto; width: 260px; font-size: 14px; }
  .totals td { padding: 4px 8px; }
  .totals .total-row td { border-top: 1px solid #1A1C1A; padding-top: 8px; font-weight: 700; font-size: 15px; }
  .foot { margin-top: 32px; padding-top: 16px; border-top: 1px solid #E5E2DC; font-size: 12px; color: #6B6E6B; text-align: center; font-style: italic; font-family: Georgia, serif; }
  .noprint { margin-bottom: 16px; }
  @media print { .noprint { display: none !important; } body { padding: 0; } }
  button { background: #1A1C1A; color: #fff; border: 0; padding: 10px 18px; border-radius: 999px; cursor: pointer; font-size: 12px; letter-spacing: 2px; text-transform: uppercase; }
</style></head><body>
  <div class="noprint"><button onclick="window.print()">Print this slip</button></div>
  <div style="display:flex;justify-content:space-between;align-items:flex-start;">
    <div>
      <div class="brand">Jenova <em style="color:#8A8D8A;">Collective</em></div>
      <div class="tag">Veteran-Founded · Woman-Owned</div>
    </div>
    <div style="text-align:right;">
      <div class="lbl">Packing Slip</div>
      <div style="font-family:'SF Mono',Menlo,monospace;font-size:15px;font-weight:700;">#${escapeHtml(d.order_ref)}</div>
      <div style="font-size:12px;color:#6B6E6B;margin-top:2px;">${escapeHtml(dt)}</div>
    </div>
  </div>
  <div class="rule"></div>
  <div class="grid">
    <div class="col">
      <div class="lbl">Ship to</div>
      ${addrLines || '<div style="color:#8A8D8A;font-style:italic;">Address unavailable — check Stripe.</div>'}
      <div style="margin-top:8px;font-size:12px;color:#6B6E6B;">${escapeHtml(d.email || "")}</div>
      ${tracking}
    </div>
    <div class="col" style="text-align:right;">
      <div class="lbl">From</div>
      <div>Jenova Collective</div>
      <div style="font-size:12px;color:#6B6E6B;">jenovacollective.co</div>
      <div style="font-size:12px;color:#6B6E6B;">hello@jenovacollective.co</div>
    </div>
  </div>
  <table>
    <thead><tr>
      <th style="width:60px;text-align:center;">Qty</th>
      <th>Item</th>
      <th style="text-align:right;width:100px;">Amount</th>
    </tr></thead>
    <tbody>${rows || '<tr><td colspan="3" style="padding:20px;text-align:center;color:#8A8D8A;font-style:italic;">No items</td></tr>'}</tbody>
  </table>
  <table class="totals">
    <tr><td>Subtotal</td><td style="text-align:right;">${money(d.amount_subtotal_cents)}</td></tr>
    ${d.amount_shipping_cents ? `<tr><td>Shipping</td><td style="text-align:right;">${money(d.amount_shipping_cents)}</td></tr>` : ""}
    ${d.amount_tax_cents ? `<tr><td>Tax</td><td style="text-align:right;">${money(d.amount_tax_cents)}</td></tr>` : ""}
    <tr class="total-row"><td>Total</td><td style="text-align:right;">${money(d.amount_total_cents)}</td></tr>
  </table>
  <div class="foot">"Behold, I am doing a new thing." — Isaiah 43:19 · Thank you for being part of the collective.</div>
</body></html>`;
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shipOrder, setShipOrder] = useState(null);
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("USPS");
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcast, setBroadcast] = useState({ subject: "", body: "", audience: "all" });
  const [audienceCounts, setAudienceCounts] = useState({ founding: 0, newsletter: 0, customers: 0, all: 0 });
  const [sending, setSending] = useState(false);
  // Shipping manager (EasyPost) dialog
  const [shipMgrOpen, setShipMgrOpen] = useState(false);
  const [shipMgrLoading, setShipMgrLoading] = useState(false);
  const [shipMgrOrder, setShipMgrOrder] = useState(null);       // fully-detailed order (from /admin/orders/{id})
  const [shipMgrRatesLoading, setShipMgrRatesLoading] = useState(false);
  const [selectedRateId, setSelectedRateId] = useState("");
  const [confirmBuyOpen, setConfirmBuyOpen] = useState(false);
  const [buying, setBuying] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [pkgOverride, setPkgOverride] = useState({ weight_oz: "", length_in: "", width_in: "", height_in: "" });

  // Manual customer message dialog
  const [messageOrder, setMessageOrder] = useState(null);
  const [messageForm, setMessageForm] = useState({ subject: "", body: "", image_url: "" });
  const [messageImage, setMessageImage] = useState(null);   // File
  const [messageImagePreview, setMessageImagePreview] = useState("");
  const [messageSending, setMessageSending] = useState(false);
  const [messageHistory, setMessageHistory] = useState([]);
  const [messageHistoryLoading, setMessageHistoryLoading] = useState(false);

  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("jenova_admin_token");
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("jenova_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
    // eslint-disable-next-line
  }, []);

  const ship = async () => {
    try {
      await api.post(`/admin/orders/${shipOrder.session_id}/ship`, {
        tracking_number: tracking || null,
        carrier: carrier || null,
      });
      toast.success("Marked shipped — customer notified");
      setShipOrder(null);
      setTracking("");
      await load();
    } catch {
      toast.error("Could not mark shipped");
    }
  };

  const printSlip = async (order) => {
    // Open the window synchronously (in the click handler) to avoid popup blockers.
    const win = window.open("", "_blank", "width=800,height=1000");
    if (!win) {
      toast.error("Popup blocked — allow popups for this site.");
      return;
    }
    win.document.write('<p style="font-family:sans-serif;padding:24px;">Preparing packing slip…</p>');
    try {
      const { data } = await api.get(`/admin/orders/${order.session_id}/packing-slip`);
      win.document.open();
      win.document.write(buildPackingSlipHtml(data));
      win.document.close();
      // Give the browser a beat to render before triggering print
      setTimeout(() => { try { win.focus(); win.print(); } catch {} }, 400);
    } catch (err) {
      win.document.body.innerHTML = '<p style="font-family:sans-serif;padding:24px;color:#b00;">Could not load packing slip.</p>';
      toast.error("Could not load packing slip");
    }
  };

  const openShipMgr = async (sessionId) => {
    setShipMgrOpen(true);
    setShipMgrLoading(true);
    setShipMgrOrder(null);
    setSelectedRateId("");
    setPkgOverride({ weight_oz: "", length_in: "", width_in: "", height_in: "" });
    try {
      const { data } = await api.get(`/admin/orders/${sessionId}`);
      setShipMgrOrder(data);
      if (data.parcel) {
        setPkgOverride({
          weight_oz: String(data.parcel.weight ?? ""),
          length_in: String(data.parcel.length ?? ""),
          width_in: String(data.parcel.width ?? ""),
          height_in: String(data.parcel.height ?? ""),
        });
      }
      if (!data.shippo_transaction_id && data.shipping_rates?.length) {
        setSelectedRateId(data.shipping_rates[0]?.id || "");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load order detail");
      setShipMgrOpen(false);
    } finally {
      setShipMgrLoading(false);
    }
  };

  const getRates = async () => {
    if (!shipMgrOrder) return;
    setShipMgrRatesLoading(true);
    try {
      const overrides = {};
      if (pkgOverride.weight_oz !== "") overrides.weight_oz = parseFloat(pkgOverride.weight_oz);
      if (pkgOverride.length_in !== "") overrides.length_in = parseFloat(pkgOverride.length_in);
      if (pkgOverride.width_in !== "") overrides.width_in = parseFloat(pkgOverride.width_in);
      if (pkgOverride.height_in !== "") overrides.height_in = parseFloat(pkgOverride.height_in);
      const body = Object.keys(overrides).length ? overrides : null;
      const { data } = await api.post(`/admin/orders/${shipMgrOrder.session_id}/shipping/rates`, body);
      setShipMgrOrder((prev) => ({
        ...prev,
        shipping_rates: data.rates || [],
        shipping_verifications: data.verifications || {},
        shippo_shipment_id: data.shipment_id,
      }));
      if (data.rates?.length) {
        setSelectedRateId(data.rates[0].id);
        toast.success(`Found ${data.rates.length} shipping options`);
      } else {
        toast.error("No rates returned — address may be invalid");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't fetch rates");
    } finally {
      setShipMgrRatesLoading(false);
    }
  };

  const openMessageDialog = async (order) => {
    setMessageOrder(order);
    setMessageForm({ subject: "", body: "", image_url: "" });
    setMessageImage(null);
    setMessageImagePreview("");
    setMessageHistory([]);
    setMessageHistoryLoading(true);
    try {
      const { data } = await api.get(`/admin/orders/${order.session_id}/messages`);
      setMessageHistory(data || []);
    } catch (err) {
      // silent
    } finally {
      setMessageHistoryLoading(false);
    }
  };

  const uploadMessageImage = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };

  const onPickMessageImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setMessageImage(f);
    setMessageImagePreview(URL.createObjectURL(f));
  };

  const sendCustomerMessage = async () => {
    if (!messageOrder) return;
    if (!messageForm.subject.trim() || !messageForm.body.trim()) {
      toast.error("Subject and body are both required.");
      return;
    }
    setMessageSending(true);
    try {
      let image_url = messageForm.image_url || "";
      if (messageImage) {
        image_url = await uploadMessageImage(messageImage);
      }
      const { data } = await api.post(`/admin/orders/${messageOrder.session_id}/message`, {
        subject: messageForm.subject.trim(),
        body: messageForm.body.trim(),
        image_url: image_url || null,
      });
      setMessageHistory((prev) => [data, ...prev]);
      setMessageForm({ subject: "", body: "", image_url: "" });
      setMessageImage(null);
      setMessageImagePreview("");
      toast.success(`Sent to ${messageOrder.email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setMessageSending(false);
    }
  };

  const buyLabel = async () => {
    if (!shipMgrOrder || !selectedRateId) return;
    setBuying(true);
    try {
      const { data } = await api.post(`/admin/orders/${shipMgrOrder.session_id}/shipping/buy-label`, { rate_id: selectedRateId });
      setShipMgrOrder((prev) => ({ ...prev, ...data }));
      setConfirmBuyOpen(false);
      const emailedTo = data?.tracking_email_queued_to;
      toast.success(
        emailedTo
          ? `Label purchased. Tracking email sent to ${emailedTo}.`
          : "Label purchased. Tracking saved to the order (no email on file — send manually)."
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Label purchase failed");
    } finally {
      setBuying(false);
    }
  };

  const resetLabel = async () => {
    if (!shipMgrOrder) return;
    if (!window.confirm("This will request a refund from Shippo for the current label and clear it from this order so you can buy a fresh one. Only unused labels within 90 days are refundable. Continue?")) return;
    try {
      const { data } = await api.post(`/admin/orders/${shipMgrOrder.session_id}/shipping/reset`);
      // Refetch full detail so the dialog re-renders in the "no label yet" state
      const { data: detail } = await api.get(`/admin/orders/${shipMgrOrder.session_id}`);
      setShipMgrOrder(detail);
      setSelectedRateId("");
      const r = data?.refund || {};
      if (r.status === "success" || r.status === "queued" || r.status === "pending") {
        toast.success(`${r.message} You can now fetch new rates and buy a fresh label.`);
      } else if (r.status === "error") {
        toast.error(r.message || "Shippo declined the refund — you can still buy a new label; refund manually from the Shippo dashboard.");
      } else {
        toast.success("Old label cleared. Fetch new rates to buy a replacement.");
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset label");
    }
  };

  // Manual shipping address editor
  const [manualAddrOpen, setManualAddrOpen] = useState(false);
  const [savingAddr, setSavingAddr] = useState(false);
  const [manualAddr, setManualAddr] = useState({ name: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US" });

  const openManualAddr = () => {
    const cur = shipMgrOrder?.ship_to || {};
    setManualAddr({
      name: cur.name || shipMgrOrder?.customer?.name || "",
      line1: cur.line1 || "",
      line2: cur.line2 || "",
      city: cur.city || "",
      state: cur.state || "",
      postal_code: cur.postal_code || "",
      country: cur.country || "US",
    });
    setManualAddrOpen(true);
  };

  const saveManualAddr = async () => {
    if (!shipMgrOrder) return;
    if (!manualAddr.line1 || !manualAddr.city || !manualAddr.state || !manualAddr.postal_code) {
      toast.error("Street, city, state and ZIP are required.");
      return;
    }
    setSavingAddr(true);
    try {
      const payload = {
        name: manualAddr.name || null,
        line1: manualAddr.line1,
        line2: manualAddr.line2 || null,
        city: manualAddr.city,
        state: manualAddr.state,
        postal_code: manualAddr.postal_code,
        country: manualAddr.country || "US",
      };
      const { data } = await api.put(`/admin/orders/${shipMgrOrder.session_id}/ship-to`, payload);
      setShipMgrOrder((prev) => ({ ...prev, ship_to: data.ship_to }));
      setManualAddrOpen(false);
      toast.success("Shipping address saved. You can now fetch rates.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSavingAddr(false);
    }
  };

  const emailTracking = async () => {
    if (!shipMgrOrder) return;
    setEmailing(true);
    try {
      await api.post(`/admin/orders/${shipMgrOrder.session_id}/shipping/send-email`);
      toast.success("Tracking email sent to the customer");
      setShipMgrOrder((prev) => ({ ...prev, shipping_status: prev.shipping_status === "label_created" ? "shipped" : prev.shipping_status }));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setEmailing(false);
    }
  };

  const selectedRate = (shipMgrOrder?.shipping_rates || []).find((r) => r.id === selectedRateId);

  const openBroadcast = async () => {
    setShowBroadcast(true);
    try {
      const { data } = await api.get("/admin/broadcast/preview");
      setAudienceCounts(data);
    } catch { /* non-fatal */ }
  };

  const sendBroadcast = async () => {
    if (!broadcast.subject || !broadcast.body) {
      toast.error("Subject and message required");
      return;
    }
    if (!window.confirm(`Send this to your ${broadcast.audience === "all" ? "entire list" : broadcast.audience}? This can't be undone.`)) return;
    setSending(true);
    try {
      const bodyHtml = broadcast.body.split("\n\n").map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("");
      const { data } = await api.post("/admin/broadcast", {
        subject: broadcast.subject,
        html: bodyHtml,
        audience: broadcast.audience,
      });
      toast.success(`Sent to ${data.sent} of ${data.audience_size} recipients`);
      setShowBroadcast(false);
      setBroadcast({ subject: "", body: "", audience: "all" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Broadcast failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-24 md:pt-28 pb-24 min-h-screen bg-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="border-b border-[#E5E2DC] pb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <Link to="/admin" className="text-sm text-stone-500 hover:text-[#1A1C1A] inline-flex items-center gap-1 mb-2">
              <ArrowLeft strokeWidth={1.5} className="w-4 h-4" /> Back to Products
            </Link>
            <p className="text-xs uppercase tracking-[0.3em] text-stone-500 font-semibold">Studio</p>
            <h1 className="mt-2 font-serif text-4xl md:text-5xl font-light tracking-tight">
              Order <em className="italic text-stone-600">dashboard</em>.
            </h1>
          </div>
          <Button data-testid="admin-broadcast-button" onClick={openBroadcast} className="bg-[#1A1C1A] hover:bg-[#3C3E3C] rounded-full">
            <Send className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Send broadcast
          </Button>
        </div>

        <div className="mt-8 bg-white border border-[#E5E2DC] rounded-xl overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-6 py-4 border-b border-[#E5E2DC] bg-[#F9F9F6] text-xs uppercase tracking-[0.2em] font-semibold text-stone-500">
            <div className="col-span-2">Date</div>
            <div className="col-span-4">Customer</div>
            <div className="col-span-1">Total</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-3 text-right">Actions</div>
          </div>
          {loading && <div className="p-8 text-center text-stone-500">Loading…</div>}
          {!loading && orders.length === 0 && (
            <div className="p-12 text-center text-stone-500 font-serif italic">No orders yet.</div>
          )}
          {orders.map((o) => (
            <div key={o.session_id} data-testid="admin-order-row" className="grid grid-cols-12 gap-3 px-6 py-4 border-b border-[#E5E2DC] items-center text-sm">
              <div className="col-span-2 text-stone-600">{new Date(o.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
              <div className="col-span-4 min-w-0">
                {o.email ? (
                  <div className="flex items-center gap-2 min-w-0">
                    <a
                      href={`mailto:${o.email}`}
                      data-testid="admin-order-email"
                      className="truncate text-[#1A1C1A] hover:underline"
                      title={o.email}
                    >
                      {o.email}
                    </a>
                    <button
                      type="button"
                      data-testid="admin-order-email-copy"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(o.email);
                        toast.success("Email copied");
                      }}
                      className="text-[10px] uppercase tracking-[0.15em] text-stone-400 hover:text-[#1A1C1A] transition-colors flex-shrink-0"
                      title="Copy email"
                    >
                      Copy
                    </button>
                  </div>
                ) : (
                  <span className="text-stone-400 italic text-xs">Awaiting…</span>
                )}
              </div>
              <div className="col-span-1">${((o.amount || 0) / 100).toFixed(2)}</div>
              <div className="col-span-2">
                <span className={`text-xs uppercase tracking-wider px-2.5 py-1 rounded-full ${
                  o.shipping_status === "shipped" ? "bg-[#EAE7DC] text-[#3F5F3F]" :
                  o.payment_status === "paid" ? "bg-blue-50 text-blue-700" :
                  o.payment_status === "failed" ? "bg-red-50 text-red-700" :
                  "bg-stone-100 text-stone-600"
                }`}>
                  {o.shipping_status === "shipped" ? "Shipped" : o.payment_status}
                </span>
              </div>
              <div className="col-span-3 flex justify-end gap-2 flex-wrap">
                {o.email && (
                  <Button
                    data-testid="admin-message-customer-button"
                    onClick={() => openMessageDialog(o)}
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                  >
                    <Mail className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Message
                  </Button>
                )}
                {o.payment_status === "paid" && (
                  <>
                    <Button
                      data-testid="admin-print-slip-button"
                      onClick={() => printSlip(o)}
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                    >
                      <Printer className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Slip
                    </Button>
                    <Button
                      data-testid="admin-manage-shipping-button"
                      onClick={() => openShipMgr(o.session_id)}
                      size="sm"
                      className="rounded-full bg-[#1A1C1A] hover:bg-[#3C3E3C]"
                    >
                      <Truck className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Ship
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Ship dialog */}
      <Dialog open={!!shipOrder} onOpenChange={(v) => !v && setShipOrder(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">Mark as shipped</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Carrier</Label>
              <Select value={carrier} onValueChange={setCarrier}>
                <SelectTrigger data-testid="ship-carrier"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USPS">USPS</SelectItem>
                  <SelectItem value="UPS">UPS</SelectItem>
                  <SelectItem value="FedEx">FedEx</SelectItem>
                  <SelectItem value="DHL">DHL</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tracking number (optional)</Label>
              <Input data-testid="ship-tracking" value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="1Z999..." />
            </div>
            <p className="text-xs text-stone-500">Sends a "shipped" email to <strong>{shipOrder?.email}</strong> with tracking info.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShipOrder(null)} className="rounded-full">Cancel</Button>
            <Button data-testid="ship-confirm" onClick={ship} className="bg-[#1A1C1A] hover:bg-[#3C3E3C] rounded-full">
              <Truck className="w-4 h-4 mr-1" strokeWidth={1.5} /> Ship & notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Broadcast dialog */}
      <Dialog open={showBroadcast} onOpenChange={setShowBroadcast}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light inline-flex items-center gap-2">
              <Send strokeWidth={1.5} className="w-5 h-5" /> Send broadcast email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Audience</Label>
              <Select value={broadcast.audience} onValueChange={(v) => setBroadcast({ ...broadcast, audience: v })}>
                <SelectTrigger data-testid="broadcast-audience"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Everyone ({audienceCounts.all})</SelectItem>
                  <SelectItem value="founding">Founding members only ({audienceCounts.founding})</SelectItem>
                  <SelectItem value="newsletter">Newsletter subscribers only ({audienceCounts.newsletter})</SelectItem>
                  <SelectItem value="customers">Customer accounts only ({audienceCounts.customers})</SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-stone-500">
                "Customer accounts" = anyone who signed in with Google. "Everyone" auto-dedupes across all three lists.
              </p>
            </div>
            <div>
              <Label>Subject line</Label>
              <Input data-testid="broadcast-subject" value={broadcast.subject} onChange={(e) => setBroadcast({ ...broadcast, subject: e.target.value })} placeholder="Doors are open — welcome to Jenova" />
            </div>
            <div>
              <Label>Message body</Label>
              <Textarea
                data-testid="broadcast-body"
                rows={10}
                value={broadcast.body}
                onChange={(e) => setBroadcast({ ...broadcast, body: e.target.value })}
                placeholder="Write like you're texting a friend. Paragraphs separated by blank lines. Line breaks preserved."
              />
              <p className="mt-1 text-xs text-stone-500">Wrapped in your Jenova branding automatically. Preview by sending to yourself first (audience = newsletter, add just your email).</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowBroadcast(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="broadcast-send" onClick={sendBroadcast} disabled={sending} className="bg-[#1A1C1A] hover:bg-[#3C3E3C] rounded-full">
              <Mail className="w-4 h-4 mr-1" strokeWidth={1.5} /> {sending ? "Sending…" : "Send now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shipping Manager dialog */}
      <Dialog open={shipMgrOpen} onOpenChange={(v) => !v && setShipMgrOpen(false)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ship-manager-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">
              Shipping · <em className="italic text-stone-500">#{shipMgrOrder?.order_ref || "—"}</em>
            </DialogTitle>
          </DialogHeader>

          {shipMgrLoading || !shipMgrOrder ? (
            <div className="py-12 text-center text-stone-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading order…
            </div>
          ) : (
            <div className="space-y-6 py-2">
              {/* Test-mode banner */}
              {shipMgrOrder.shippo_mode === "test" && (
                <div className="p-3 rounded-lg bg-[#F4E9D8] border border-[#B8935A] text-[#5A4020] text-sm" data-testid="test-mode-banner">
                  <div className="font-semibold flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" strokeWidth={2} /> Shippo is in TEST MODE
                  </div>
                  <div className="text-xs mt-1">
                    Every label you print here will be watermarked <strong>"SAMPLE — DO NOT MAIL"</strong> and cannot be scanned by USPS. This is expected and lets you practice for free.
                    To buy real, mailable labels: paste your <code className="bg-[#EFDBB8] px-1 rounded">shippo_live_...</code> key into the backend (add billing to Shippo first).
                  </div>
                </div>
              )}
              {shipMgrOrder.shippo_mode === "not_configured" && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                  <div className="font-semibold flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" strokeWidth={2} /> Shippo not configured
                  </div>
                  <div className="text-xs mt-1">Add SHIPPO_API_KEY to backend .env to enable rates and labels.</div>
                </div>
              )}
              {/* Address verification warning */}
              {shipMgrOrder.shipping_verifications?.delivery?.success === false && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm flex gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <div className="font-semibold">Address may be invalid</div>
                    <div className="text-xs mt-1">
                      {shipMgrOrder.shipping_verifications.delivery.errors?.map((e) => e.message).join("; ") || "EasyPost couldn't verify this address. Double-check with the customer before buying a label."}
                    </div>
                  </div>
                </div>
              )}

              {/* Customer + Ship-to */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-[#E5E2DC] rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-stone-500 mb-2">Customer</p>
                  <p className="text-sm font-semibold">{shipMgrOrder.customer?.name || shipMgrOrder.ship_to?.name || "—"}</p>
                  {shipMgrOrder.customer?.email ? (
                    <div className="flex items-center gap-2 mt-0.5">
                      <a
                        href={`mailto:${shipMgrOrder.customer.email}`}
                        data-testid="ship-mgr-customer-email"
                        className="text-sm text-[#1A1C1A] hover:underline truncate"
                      >
                        {shipMgrOrder.customer.email}
                      </a>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(shipMgrOrder.customer.email);
                          toast.success("Email copied");
                        }}
                        className="text-[10px] uppercase tracking-[0.15em] text-stone-400 hover:text-[#1A1C1A]"
                      >
                        Copy
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm text-stone-400 italic mt-0.5">Email will populate after payment settles</p>
                  )}
                </div>
                <div className="border border-[#E5E2DC] rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-stone-500">Ship to</p>
                    <button
                      data-testid="edit-ship-to-button"
                      type="button"
                      onClick={openManualAddr}
                      className="text-[11px] uppercase tracking-[0.15em] text-stone-500 hover:text-[#1A1C1A] inline-flex items-center gap-1"
                    >
                      <Pencil strokeWidth={1.5} className="w-3 h-3" />
                      {shipMgrOrder.ship_to ? "Edit" : "Add address"}
                    </button>
                  </div>
                  {shipMgrOrder.ship_to ? (
                    <div className="text-sm text-stone-700 leading-snug">
                      {shipMgrOrder.ship_to.name && <div>{shipMgrOrder.ship_to.name}</div>}
                      <div>{shipMgrOrder.ship_to.line1}</div>
                      {shipMgrOrder.ship_to.line2 && <div>{shipMgrOrder.ship_to.line2}</div>}
                      <div>{[shipMgrOrder.ship_to.city, shipMgrOrder.ship_to.state, shipMgrOrder.ship_to.postal_code].filter(Boolean).join(", ")}</div>
                      <div>{shipMgrOrder.ship_to.country}</div>
                    </div>
                  ) : (
                    <div className="text-sm">
                      <p className="text-stone-400 italic mb-2">No shipping address on file</p>
                      <Button
                        data-testid="add-ship-to-button"
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={openManualAddr}
                        className="rounded-full h-8 text-xs"
                      >
                        <MapPin strokeWidth={1.5} className="w-3.5 h-3.5 mr-1" /> Enter address manually
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Items */}
              <div className="border border-[#E5E2DC] rounded-xl overflow-hidden">
                <div className="px-4 py-2 bg-[#F9F9F6] text-[10px] uppercase tracking-[0.2em] font-semibold text-stone-500 grid grid-cols-12 gap-2">
                  <div className="col-span-1">Qty</div>
                  <div className="col-span-8">Item</div>
                  <div className="col-span-3 text-right">Amount</div>
                </div>
                {shipMgrOrder.items.map((it, i) => (
                  <div key={i} className="px-4 py-3 border-t border-[#E5E2DC] grid grid-cols-12 gap-2 text-sm items-center">
                    <div className="col-span-1 font-semibold">{it.quantity}</div>
                    <div className="col-span-8">
                      <div className="font-medium">{it.name}</div>
                      <div className="text-xs text-stone-500 mt-0.5">
                        {[it.size && `Size ${it.size}`, it.color, it.weight_oz && `${it.weight_oz} oz`].filter(Boolean).join(" · ") || "—"}
                      </div>
                    </div>
                    <div className="col-span-3 text-right">{it.unit_price != null ? `$${(it.unit_price * it.quantity).toFixed(2)}` : "—"}</div>
                  </div>
                ))}
              </div>

              {/* Cost breakdown */}
              <div className="border border-[#E5E2DC] rounded-xl p-4 text-sm">
                <div className="flex justify-between py-1"><span className="text-stone-600">Subtotal</span><span>{money(shipMgrOrder.amount_subtotal_cents)}</span></div>
                <div className="flex justify-between py-1"><span className="text-stone-600">Shipping paid by customer</span><span>{money(shipMgrOrder.amount_shipping_cents)}</span></div>
                {shipMgrOrder.amount_tax_cents > 0 && <div className="flex justify-between py-1"><span className="text-stone-600">Tax</span><span>{money(shipMgrOrder.amount_tax_cents)}</span></div>}
                <div className="flex justify-between py-2 mt-1 border-t border-[#E5E2DC] font-semibold"><span>Total paid</span><span>{money(shipMgrOrder.amount_total_cents)}</span></div>
                {shipMgrOrder.shipping_cost_cents != null && (
                  <div className="flex justify-between py-1 text-xs text-stone-500 mt-2 pt-2 border-t border-[#E5E2DC]">
                    <span>Label cost</span>
                    <span>{money(shipMgrOrder.shipping_cost_cents)}</span>
                  </div>
                )}
              </div>

              {/* Package dims (editable before buy) */}
              {!shipMgrOrder.shippo_transaction_id && (
                <div className="border border-[#E5E2DC] rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-stone-500 mb-2">Package (editable)</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div><Label className="text-xs">Weight (oz)</Label><Input data-testid="pkg-override-weight" type="number" step="0.1" value={pkgOverride.weight_oz} onChange={(e) => setPkgOverride({ ...pkgOverride, weight_oz: e.target.value })} /></div>
                    <div><Label className="text-xs">Length (in)</Label><Input data-testid="pkg-override-length" type="number" step="0.1" value={pkgOverride.length_in} onChange={(e) => setPkgOverride({ ...pkgOverride, length_in: e.target.value })} /></div>
                    <div><Label className="text-xs">Width (in)</Label><Input data-testid="pkg-override-width" type="number" step="0.1" value={pkgOverride.width_in} onChange={(e) => setPkgOverride({ ...pkgOverride, width_in: e.target.value })} /></div>
                    <div><Label className="text-xs">Height (in)</Label><Input data-testid="pkg-override-height" type="number" step="0.1" value={pkgOverride.height_in} onChange={(e) => setPkgOverride({ ...pkgOverride, height_in: e.target.value })} /></div>
                  </div>
                </div>
              )}

              {/* Rates OR Label info */}
              {shipMgrOrder.shippo_transaction_id ? (
                <div className="border border-[#3F5F3F]/30 bg-[#EAE7DC]/50 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Check className="w-4 h-4 text-[#3F5F3F]" strokeWidth={2} />
                    <p className="text-sm font-semibold text-[#3F5F3F]">Label purchased</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div><span className="text-stone-500 text-xs">Carrier · Service</span><div>{shipMgrOrder.carrier} · {shipMgrOrder.service}</div></div>
                    <div><span className="text-stone-500 text-xs">Tracking</span><div className="font-mono text-xs break-all">{shipMgrOrder.tracking_number}</div></div>
                    <div><span className="text-stone-500 text-xs">Label cost</span><div>{money(shipMgrOrder.shipping_cost_cents)}</div></div>
                    <div><span className="text-stone-500 text-xs">Created</span><div>{shipMgrOrder.label_created_at ? new Date(shipMgrOrder.label_created_at).toLocaleString() : "—"}</div></div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {shipMgrOrder.label_url && (
                      <a href={shipMgrOrder.label_url} target="_blank" rel="noreferrer" data-testid="print-label-link">
                        <Button size="sm" className="bg-[#1A1C1A] hover:bg-[#3C3E3C] rounded-full">
                          <Printer className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />
                          {shipMgrOrder.shippo_mode === "test" ? "Print label (SAMPLE — do not mail)" : "Print label"}
                        </Button>
                      </a>
                    )}
                    {shipMgrOrder.tracking_url && (
                      <a href={shipMgrOrder.tracking_url} target="_blank" rel="noreferrer" data-testid="view-tracking-link">
                        <Button size="sm" variant="outline" className="rounded-full">
                          <ExternalLink className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> View tracking
                        </Button>
                      </a>
                    )}
                    <Button size="sm" variant="outline" onClick={emailTracking} disabled={emailing} data-testid="email-tracking-button" className="rounded-full">
                      <Mail className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> {emailing ? "Sending…" : shipMgrOrder.tracking_email_sent_at ? "Resend tracking email" : "Email tracking to customer"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={resetLabel}
                      data-testid="reset-label-button"
                      className="rounded-full border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                    >
                      <RotateCcw className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Refund & void label
                    </Button>
                  </div>
                  {shipMgrOrder.tracking_email_sent_at && (
                    <p className="mt-3 text-xs text-green-700 flex items-center gap-1.5" data-testid="tracking-email-sent-hint">
                      <Check className="w-3.5 h-3.5" strokeWidth={2} />
                      Tracking email auto-sent to {shipMgrOrder.tracking_email_to || "customer"} on
                      {" "}{new Date(shipMgrOrder.tracking_email_sent_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}.
                    </p>
                  )}
                  {!shipMgrOrder.tracking_email_sent_at && shipMgrOrder.tracking_email_queued_to && (
                    <p className="mt-3 text-xs text-stone-600 flex items-center gap-1.5">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2} />
                      Sending tracking email to {shipMgrOrder.tracking_email_queued_to}…
                    </p>
                  )}
                  {shipMgrOrder.shippo_mode === "test" && (
                    <p className="mt-3 text-xs text-red-700">
                      Heads up: this label was minted while Shippo was in <strong>test mode</strong>, so it prints
                      "SAMPLE — DO NOT MAIL" and can't be scanned by USPS. Click <em>Refund &amp; void label</em>
                      above — we'll ask Shippo to refund it, clear it from this order, then you can Get rates and
                      buy a real one.
                    </p>
                  )}
                </div>
              ) : shipMgrOrder.shipping_rates?.length > 0 ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-stone-500">Available rates</p>
                    <Button size="sm" variant="outline" onClick={getRates} disabled={shipMgrRatesLoading} className="rounded-full h-7 text-xs">
                      {shipMgrRatesLoading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Refreshing</> : "Refresh rates"}
                    </Button>
                  </div>
                  <div className="border border-[#E5E2DC] rounded-xl divide-y divide-[#E5E2DC]" data-testid="rates-list">
                    {shipMgrOrder.shipping_rates.map((r) => (
                      <label key={r.id} className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-[#F9F9F6] ${selectedRateId === r.id ? "bg-[#F4F1EA]" : ""}`}>
                        <input type="radio" name="rate" value={r.id} checked={selectedRateId === r.id} onChange={() => setSelectedRateId(r.id)} data-testid={`rate-option-${r.id}`} />
                        <div className="flex-1">
                          <div className="text-sm font-semibold">{r.carrier} · {r.service}</div>
                          <div className="text-xs text-stone-500">
                            {r.delivery_days ? `${r.delivery_days} business day${r.delivery_days === 1 ? "" : "s"}` : "Delivery est. unknown"}
                          </div>
                        </div>
                        <div className="text-sm font-semibold">${parseFloat(r.rate).toFixed(2)}</div>
                      </label>
                    ))}
                  </div>
                  <div className="mt-4 flex justify-end">
                    <Button
                      onClick={() => setConfirmBuyOpen(true)}
                      disabled={!selectedRateId}
                      data-testid="create-label-button"
                      className="bg-[#1A1C1A] hover:bg-[#3C3E3C] rounded-full"
                    >
                      <DollarSign className="w-4 h-4 mr-1" strokeWidth={1.5} /> Create shipping label
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <Button
                    onClick={getRates}
                    disabled={shipMgrRatesLoading || !shipMgrOrder.ship_to}
                    data-testid="get-rates-button"
                    className="bg-[#1A1C1A] hover:bg-[#3C3E3C] rounded-full"
                  >
                    {shipMgrRatesLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Fetching rates…</> : <><Package className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Get shipping rates</>}
                  </Button>
                  {!shipMgrOrder.ship_to && (
                    <div className="mt-2 space-y-2">
                      <p className="text-xs text-red-600">No shipping address on file for this order.</p>
                      <Button
                        data-testid="add-ship-to-inline"
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={openManualAddr}
                        className="rounded-full h-8 text-xs"
                      >
                        <MapPin strokeWidth={1.5} className="w-3.5 h-3.5 mr-1" /> Enter address manually
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShipMgrOpen(false)} className="rounded-full">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm buy label dialog */}
      <Dialog open={confirmBuyOpen} onOpenChange={(v) => !v && setConfirmBuyOpen(false)}>
        <DialogContent className="max-w-md" data-testid="confirm-buy-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">Purchase shipping label?</DialogTitle>
          </DialogHeader>
          {selectedRate && (
            <div className="py-3">
              <div className="rounded-lg border border-[#E5E2DC] p-4 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-stone-500">Carrier</span><span className="font-semibold">{selectedRate.carrier}</span></div>
                <div className="flex justify-between"><span className="text-stone-500">Service</span><span className="font-semibold">{selectedRate.service}</span></div>
                {selectedRate.delivery_days && <div className="flex justify-between"><span className="text-stone-500">Delivery</span><span>{selectedRate.delivery_days} business day{selectedRate.delivery_days === 1 ? "" : "s"}</span></div>}
                <div className="flex justify-between pt-2 mt-2 border-t border-[#E5E2DC]"><span className="text-stone-500">Amount to charge your EasyPost balance</span><span className="text-lg font-serif">${parseFloat(selectedRate.rate).toFixed(2)}</span></div>
              </div>
              <p className="text-xs text-stone-500 mt-3">Once purchased, this label cannot be undone. Void within 30 days from your EasyPost dashboard if unused.</p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmBuyOpen(false)} disabled={buying} className="rounded-full">Cancel</Button>
            <Button onClick={buyLabel} disabled={buying || !selectedRate} data-testid="confirm-purchase-label-button" className="bg-[#1A1C1A] hover:bg-[#3C3E3C] rounded-full">
              {buying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Purchasing…</> : `Purchase label · $${selectedRate ? parseFloat(selectedRate.rate).toFixed(2) : "—"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual customer message dialog */}
      <Dialog open={!!messageOrder} onOpenChange={(v) => !v && setMessageOrder(null)}>
        <DialogContent className="max-w-2xl" data-testid="message-customer-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">Message customer</DialogTitle>
            {messageOrder?.email && (
              <p className="text-xs text-stone-500 mt-1">
                To: <a href={`mailto:${messageOrder.email}`} className="text-[#1A1C1A] hover:underline">{messageOrder.email}</a>
                {" · "}Order {messageOrder.session_id?.slice(-10).toUpperCase()}
              </p>
            )}
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 py-2">
            <div className="md:col-span-3 space-y-3">
              <div>
                <Label className="text-xs">Subject</Label>
                <Input
                  data-testid="message-subject"
                  value={messageForm.subject}
                  onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                  placeholder="Quick update on your order"
                  maxLength={200}
                />
              </div>
              <div>
                <Label className="text-xs">Message</Label>
                <Textarea
                  data-testid="message-body"
                  rows={7}
                  value={messageForm.body}
                  onChange={(e) => setMessageForm({ ...messageForm, body: e.target.value })}
                  placeholder="Hey! Wanted to let you know your order is running a day behind, but it will ship first thing tomorrow. Thanks for your patience."
                />
                <p className="text-[10px] text-stone-400 mt-1">Plain text is fine — line breaks are preserved automatically.</p>
              </div>
              <div>
                <Label className="text-xs">Attach image (optional)</Label>
                <div className="flex items-center gap-3">
                  <input
                    data-testid="message-image-input"
                    type="file"
                    accept="image/*"
                    onChange={onPickMessageImage}
                    className="text-xs"
                  />
                  {messageImagePreview && (
                    <img src={messageImagePreview} alt="preview" className="h-16 w-16 object-cover rounded-md border border-[#E5E2DC]" />
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setMessageOrder(null)} disabled={messageSending} className="rounded-full">Cancel</Button>
                <Button
                  data-testid="message-send-button"
                  onClick={sendCustomerMessage}
                  disabled={messageSending}
                  className="rounded-full bg-[#1A1C1A] hover:bg-[#3C3E3C]"
                >
                  {messageSending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send</>}
                </Button>
              </div>
            </div>
            <div className="md:col-span-2 border-l border-[#E5E2DC] md:pl-6">
              <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-stone-500 mb-3">Previously sent</p>
              {messageHistoryLoading && <p className="text-xs text-stone-400 italic">Loading…</p>}
              {!messageHistoryLoading && messageHistory.length === 0 && (
                <p className="text-xs text-stone-400 italic">No messages sent yet on this order.</p>
              )}
              <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1" data-testid="message-history">
                {messageHistory.map((m) => (
                  <div key={m.id} className="border border-[#E5E2DC] rounded-lg p-3 text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-[#1A1C1A] truncate">{m.subject}</span>
                      <span className="text-stone-400 flex-shrink-0 ml-2">{new Date(m.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                    </div>
                    <p className="text-stone-600 line-clamp-3 whitespace-pre-wrap">{m.body}</p>
                    {m.image_url && (
                      <img src={m.image_url.startsWith("http") ? m.image_url : `${(process.env.REACT_APP_BACKEND_URL || "")}${m.image_url}`} alt="" className="mt-2 rounded max-h-24 object-cover" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual shipping address dialog */}
      <Dialog open={manualAddrOpen} onOpenChange={(v) => !v && setManualAddrOpen(false)}>
        <DialogContent className="max-w-lg" data-testid="manual-address-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">
              {shipMgrOrder?.ship_to ? "Edit shipping address" : "Add shipping address"}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-xs text-stone-500 leading-relaxed">
              Use this when the customer's address didn't come through at checkout, or they emailed you a
              correction. This address wins over anything Stripe returned and will be used to fetch rates
              and print the label.
            </p>
            <div><Label className="text-xs">Recipient name</Label><Input data-testid="manual-addr-name" value={manualAddr.name} onChange={(e) => setManualAddr({ ...manualAddr, name: e.target.value })} placeholder="Jane Doe" /></div>
            <div><Label className="text-xs">Street address</Label><Input data-testid="manual-addr-line1" value={manualAddr.line1} onChange={(e) => setManualAddr({ ...manualAddr, line1: e.target.value })} placeholder="123 Main St" /></div>
            <div><Label className="text-xs">Apt / Suite (optional)</Label><Input data-testid="manual-addr-line2" value={manualAddr.line2} onChange={(e) => setManualAddr({ ...manualAddr, line2: e.target.value })} placeholder="Unit 4B" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">City</Label><Input data-testid="manual-addr-city" value={manualAddr.city} onChange={(e) => setManualAddr({ ...manualAddr, city: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">State</Label><Input data-testid="manual-addr-state" value={manualAddr.state} onChange={(e) => setManualAddr({ ...manualAddr, state: e.target.value.toUpperCase().slice(0, 2) })} maxLength={2} placeholder="CA" /></div>
                <div><Label className="text-xs">ZIP</Label><Input data-testid="manual-addr-zip" value={manualAddr.postal_code} onChange={(e) => setManualAddr({ ...manualAddr, postal_code: e.target.value })} placeholder="90210" /></div>
              </div>
            </div>
            <div><Label className="text-xs">Country</Label><Input data-testid="manual-addr-country" value={manualAddr.country} onChange={(e) => setManualAddr({ ...manualAddr, country: e.target.value.toUpperCase().slice(0, 2) })} maxLength={2} placeholder="US" /></div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setManualAddrOpen(false)} disabled={savingAddr} className="rounded-full">Cancel</Button>
            <Button onClick={saveManualAddr} disabled={savingAddr} data-testid="manual-addr-save" className="bg-[#1A1C1A] hover:bg-[#3C3E3C] rounded-full">
              {savingAddr ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Save address"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
